import React from "react";
import styled from "styled-components";
import { Button, Tag } from "../../../../../design-system";
import {
  ContentConcept,
  SocialLearningMemory,
  TrendReport,
} from "../../../../../database/community/contentEngine";
import { ScheduledPost } from "../../../../../database/community/scheduledPosts";
import { platformLabel } from "../../publish/SchedulePostDialog";
import { useMediaStudio } from "../../context/MediaStudioContext";
import { Card, CardHint, ExternalLink, Muted, Row, RowList, Spacer, StatusDot, Thumb, STATUS_COLOR, projectThumb } from "../shared";
import Modal from "../Modal";
import {
  AgentFact,
  analystFact,
  scriptwriterFact,
  timeLabel,
  trendwatcherFact,
} from "./homeDerive";

// The pipeline as a team: one card per stage, facts from real Firestore
// timestamps. These are scheduled runs, not live processes, so cards state
// what last happened and never fake activity. Every card expands into the
// records behind its numbers.

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: var(--space-2);
`;

const AgentCard = styled(Card)<{ $attention?: boolean; $expanded?: boolean }>`
  gap: var(--space-2);
  padding: var(--space-3);
  cursor: pointer;
  border-color: ${(p) =>
    p.$attention
      ? "var(--color-warning, #d0a020)"
      : p.$expanded
      ? "var(--color-border-strong)"
      : "var(--color-border-subtle)"};

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

const AgentName = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Role = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-tertiary);
`;

const Fact = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlockTitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const ClickRow = styled(Row)<{ $clickable?: boolean }>`
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  &:hover {
    background: ${(p) => (p.$clickable ? "var(--color-surface-subtle)" : "transparent")};
  }
`;

const Summary = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  white-space: pre-wrap;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  padding: 3px 8px;
`;

const factLine = (f: AgentFact) => (
  <>
    <Fact>{f.headline}</Fact>
    {f.when && <Muted>{timeLabel(f.when)}</Muted>}
  </>
);

export type AgentTeamRowProps = {
  reports: TrendReport[];
  concepts: ContentConcept[];
  learning: SocialLearningMemory | null;
  expanded: string | null;
  onExpand: (agent: string | null) => void;
  onNavigate: (path: string) => void;
  onSelectPost: (postId: string) => void;
};

export const AgentTeamRow: React.FC<AgentTeamRowProps> = ({
  reports,
  concepts,
  learning,
  expanded,
  onExpand,
  onNavigate,
  onSelectPost,
}) => {
  const { posts, projects } = useMediaStudio();

  const trend = trendwatcherFact(reports);
  const script = scriptwriterFact(concepts);
  const analyst = analystFact(concepts);

  const drafts = posts.drafts.length;
  const queued = posts.upcoming.length;
  const nextSlot = posts.upcoming[0]?.dueAt || null;
  const latestReport = reports[0];

  const toggle = (agent: string) => onExpand(expanded === agent ? null : agent);

  const postById = new Map<string, ScheduledPost>(
    posts.posts.map((p) => [p.id, p])
  );

  const verdictTag = (c: ContentConcept) => {
    const v = c.verdict || c.suggestedVerdict;
    if (!v) return <Tag variant="neutral" size="sm">{c.status}</Tag>;
    const variant =
      v === "SCALE" ? "success" : v === "ITERATE" ? "warning" : "danger";
    return (
      <Tag variant={variant} size="sm">
        {v.toLowerCase()}
      </Tag>
    );
  };

  const postRow = (p: ScheduledPost, right: string) => {
    const thumb =
      projectThumb(p.projectId ? projects.projectById.get(p.projectId) : undefined) ||
      p.mediaUrls[0];
    return (
      <ClickRow key={p.id} $clickable onClick={() => onSelectPost(p.id)}>
        <StatusDot $color={STATUS_COLOR[p.status]} />
        {thumb ? <Thumb src={thumb} alt="" /> : null}
        <div style={{ minWidth: 0, flex: 1 }}>
          <Fact style={{ WebkitLineClamp: 1 }}>
            {p.caption?.trim() || "Untitled post"}
          </Fact>
          <Muted>
            {timeLabel(p.dueAt)} · {p.platforms.map(platformLabel).join(", ")}
          </Muted>
        </div>
        <Muted>{right}</Muted>
      </ClickRow>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <Muted style={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Your content team
      </Muted>
      <Grid>
        <AgentCard
          $expanded={expanded === "trendwatcher"}
          onClick={() => toggle("trendwatcher")}
        >
          <div>
            <AgentName>Trendwatcher</AgentName>
            <Role>Niche research</Role>
          </div>
          {factLine(trend)}
        </AgentCard>

        <AgentCard
          $expanded={expanded === "scriptwriter"}
          onClick={() => toggle("scriptwriter")}
        >
          <div>
            <AgentName>Hook and script</AgentName>
            <Role>Concepts and hooks</Role>
          </div>
          {factLine(script)}
        </AgentCard>

        <AgentCard
          $expanded={expanded === "producer"}
          onClick={() => toggle("producer")}
        >
          <div>
            <AgentName>Producer</AgentName>
            <Role>Renders and media</Role>
          </div>
          <Fact>
            {projects.projects.length === 0
              ? "No rendered projects yet. Composed slides and reels land in the library."
              : `${projects.projects.length} project${
                  projects.projects.length === 1 ? "" : "s"
                } in the library`}
          </Fact>
        </AgentCard>

        <AgentCard
          $attention={drafts > 0}
          $expanded={expanded === "planner"}
          onClick={() => toggle("planner")}
        >
          <div>
            <AgentName>
              Planner
              {drafts > 0 && (
                <Tag variant="warning" size="sm" withDot style={{ marginLeft: 6 }}>
                  Needs you
                </Tag>
              )}
            </AgentName>
            <Role>Queue and schedule</Role>
          </div>
          <Fact>
            {drafts > 0
              ? `${drafts} draft${drafts === 1 ? "" : "s"} waiting for review, ${queued} queued`
              : queued > 0
              ? `${queued} post${queued === 1 ? "" : "s"} queued`
              : "Queue is empty. Approve a draft or schedule from the library."}
          </Fact>
          {nextSlot && <Muted>Next: {timeLabel(nextSlot)}</Muted>}
        </AgentCard>

        <AgentCard
          $expanded={expanded === "solo"}
          onClick={() => toggle("solo")}
        >
          <div>
            <AgentName>SOLO</AgentName>
            <Role>Self-improvement loop</Role>
          </div>
          <Fact>
            {learning?.activeExperiment
              ? `Testing ${learning.activeExperiment.mutationDimension || "one change"} on ${platformLabel(
                  learning.activeExperiment.platform || ""
                )}`
              : learning?.promotedPatterns.length
              ? `${learning.promotedPatterns.length} proven pattern${
                  learning.promotedPatterns.length === 1 ? "" : "s"
                } in memory`
              : "No active learning test yet. SOLO waits for comparable post metrics."}
          </Fact>
          {learning?.updatedAt && <Muted>{timeLabel(learning.updatedAt)}</Muted>}
        </AgentCard>

        <AgentCard
          $expanded={expanded === "analyst"}
          onClick={() => toggle("analyst")}
        >
          <div>
            <AgentName>Analyst</AgentName>
            <Role>Verdicts and metrics</Role>
          </div>
          {analyst.empty ? (
            factLine(analyst)
          ) : (
            <>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Tag variant="success" size="sm">
                  {analyst.counts.scale} scale
                </Tag>
                <Tag variant="warning" size="sm">
                  {analyst.counts.iterate} iterate
                </Tag>
                <Tag variant="danger" size="sm">
                  {analyst.counts.kill} kill
                </Tag>
              </div>
              {analyst.when && <Muted>Metrics {timeLabel(analyst.when)}</Muted>}
            </>
          )}
        </AgentCard>
      </Grid>

      {expanded === "trendwatcher" && latestReport && (
        <Modal title="Trendwatcher research" onClose={() => onExpand(null)}>
          {latestReport.summary && (
            <>
              <BlockTitle>Research summary</BlockTitle>
              <Summary>{latestReport.summary}</Summary>
            </>
          )}
          {latestReport.queries.length > 0 && (
            <>
              <BlockTitle>Search phrases scanned</BlockTitle>
              <ChipRow>
                {latestReport.queries.map((q) => (
                  <Chip key={q}>{q}</Chip>
                ))}
              </ChipRow>
            </>
          )}
          {latestReport.hookFormulas.length > 0 && (
            <>
              <BlockTitle>Hook formulas that worked</BlockTitle>
              <RowList>
                {latestReport.hookFormulas.map((h, i) => (
                  <Row key={i}>
                    <Fact style={{ WebkitLineClamp: 2 }}>{h}</Fact>
                  </Row>
                ))}
              </RowList>
            </>
          )}
          {latestReport.risingSounds.length > 0 && (
            <>
              <BlockTitle>Rising sounds</BlockTitle>
              <ChipRow>
                {latestReport.risingSounds.map((s, i) =>
                  s.url ? (
                    <ExternalLink key={i} href={s.url}>
                      <Chip style={{ cursor: "pointer" }}>{s.label}</Chip>
                    </ExternalLink>
                  ) : (
                    <Chip key={i}>{s.label}</Chip>
                  )
                )}
              </ChipRow>
            </>
          )}
          <CardHint>
            Report {latestReport.id}
            {latestReport.source ? ` · source: ${latestReport.source}` : ""}
            {typeof latestReport.analyzedCount === "number"
              ? ` · ${latestReport.analyzedCount} posts analyzed`
              : ""}
            . Outliers and account ranking are in the niche panel above.
          </CardHint>
        </Modal>
      )}

      {expanded === "scriptwriter" && concepts.length > 0 && (
        <Modal title="Latest concepts" onClose={() => onExpand(null)}>
          <RowList>
            {concepts.slice(0, 8).map((c) => {
              const linked = c.scheduledPostIds.find((id) => postById.has(id));
              return (
                <ClickRow
                  key={c.id}
                  $clickable={Boolean(linked)}
                  onClick={() => linked && onSelectPost(linked)}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Fact style={{ WebkitLineClamp: 1 }}>{c.hook || c.concept}</Fact>
                    <Muted>
                      {c.concept}
                      {c.createdAt ? ` · ${timeLabel(c.createdAt)}` : ""}
                    </Muted>
                  </div>
                  {verdictTag(c)}
                  {linked && <Muted>View post</Muted>}
                </ClickRow>
              );
            })}
          </RowList>
        </Modal>
      )}

      {expanded === "producer" && (
        <Modal title="Rendered projects" onClose={() => onExpand(null)}>
          {projects.projects.length === 0 ? (
            <CardHint>
              Nothing rendered yet. Compose slides or a reel and it lands here.
            </CardHint>
          ) : (
            <RowList>
              {projects.projects.slice(0, 8).map((pr) => {
                const thumb = projectThumb(pr);
                return (
                  <ClickRow
                    key={pr.id}
                    $clickable
                    onClick={() => onNavigate("/media-studio/library")}
                  >
                    {thumb ? <Thumb src={thumb} alt="" /> : null}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Fact style={{ WebkitLineClamp: 1 }}>
                        {pr.title || (pr.kind === "reel" ? "Reel" : "Carousel")}
                      </Fact>
                      {pr.createdAt && <Muted>{timeLabel(pr.createdAt)}</Muted>}
                    </div>
                    <Muted>Open in library</Muted>
                  </ClickRow>
                );
              })}
            </RowList>
          )}
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onNavigate("/media-studio/library")}
            >
              Open library
            </Button>
          </div>
        </Modal>
      )}

      {expanded === "planner" && (
        <Modal title="Queue and drafts" onClose={() => onExpand(null)}>
          {drafts > 0 && (
            <>
              <BlockTitle>Waiting for review</BlockTitle>
              <RowList>{posts.drafts.slice(0, 5).map((p) => postRow(p, "draft"))}</RowList>
            </>
          )}
          {queued > 0 && (
            <>
              <BlockTitle>Queued</BlockTitle>
              <RowList>
                {posts.upcoming.slice(0, 5).map((p) => postRow(p, "queued"))}
              </RowList>
            </>
          )}
          {drafts === 0 && queued === 0 && (
            <CardHint>Queue is empty. Approve a draft or schedule from the library.</CardHint>
          )}
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            {drafts > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onNavigate("/media-studio/review")}
              >
                Review drafts
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onNavigate("/media-studio/calendar")}
            >
              Open calendar
            </Button>
          </div>
        </Modal>
      )}

      {expanded === "analyst" && !analyst.empty && (
        <Modal title="Verdicts" onClose={() => onExpand(null)}>
          <RowList>
            {concepts
              .filter((c) => c.verdict || c.suggestedVerdict)
              .slice(0, 8)
              .map((c) => {
                const linked = c.scheduledPostIds.find((id) => postById.has(id));
                return (
                  <ClickRow
                    key={c.id}
                    $clickable={Boolean(linked)}
                    onClick={() => linked && onSelectPost(linked)}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Fact style={{ WebkitLineClamp: 1 }}>{c.concept}</Fact>
                    </div>
                    {verdictTag(c)}
                    <Spacer />
                    {linked && <Muted>View post</Muted>}
                  </ClickRow>
                );
              })}
          </RowList>
        </Modal>
      )}

      {expanded === "solo" && (
        <Modal title="SOLO learning memory" onClose={() => onExpand(null)}>
          {learning?.activeExperiment && (
            <>
              <BlockTitle>Active controlled test</BlockTitle>
              <Summary>
                {learning.activeExperiment.candidateBrief ||
                  "Candidate treatment is measuring."}
              </Summary>
              <CardHint>
                {learning.activeExperiment.mutationDimension || "one variable"} ·{" "}
                {platformLabel(learning.activeExperiment.platform || "")} ·{" "}
                {learning.activeExperiment.primaryMetric || "primary metric"}
                {learning.activeExperiment.measurementWindowDays
                  ? ` · day ${learning.activeExperiment.measurementWindowDays} window`
                  : ""}
              </CardHint>
            </>
          )}
          {learning?.promotedPatterns.length ? (
            <>
              <BlockTitle>Proven patterns</BlockTitle>
              <RowList>
                {learning.promotedPatterns.map((pattern) => (
                  <Row key={pattern.signature}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Fact>{pattern.treatment}</Fact>
                      <Muted>
                        {platformLabel(pattern.platform)} ·{" "}
                        {pattern.positiveEvidence} independent wins
                        {pattern.measurementWindowDays
                          ? ` · day ${pattern.measurementWindowDays}`
                          : ""}
                      </Muted>
                    </div>
                    <Tag variant="success" size="sm">
                      proven
                    </Tag>
                  </Row>
                ))}
              </RowList>
            </>
          ) : null}
          {learning?.avoidPatterns.length ? (
            <>
              <BlockTitle>Avoid</BlockTitle>
              <RowList>
                {learning.avoidPatterns.map((pattern) => (
                  <Row key={pattern.signature}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Fact>{pattern.treatment}</Fact>
                      <Muted>{platformLabel(pattern.platform)}</Muted>
                    </div>
                    <Tag variant="danger" size="sm">
                      avoid
                    </Tag>
                  </Row>
                ))}
              </RowList>
            </>
          ) : null}
          {learning?.observations.length ? (
            <>
              <BlockTitle>Recent decisions</BlockTitle>
              <RowList>
                {learning.observations.slice(0, 8).map((observation) => (
                  <Row key={observation.id}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Fact>{observation.candidateBrief}</Fact>
                      <Muted>
                        {observation.verdict.replace(/_/g, " ")} ·{" "}
                        {observation.relativeLift >= 0 ? "+" : ""}
                        {(observation.relativeLift * 100).toFixed(1)}%
                        {observation.measurementWindowDays
                          ? ` · day ${observation.measurementWindowDays}`
                          : ""}
                      </Muted>
                    </div>
                  </Row>
                ))}
              </RowList>
            </>
          ) : null}
          {!learning?.activeExperiment &&
            !learning?.promotedPatterns.length &&
            !learning?.avoidPatterns.length &&
            !learning?.observations.length && (
              <CardHint>
                SOLO has no learning history yet. The weekly content agent will
                open a controlled task after enough comparable metrics exist.
              </CardHint>
            )}
        </Modal>
      )}
    </div>
  );
};

export default AgentTeamRow;
